import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Spin, message, Dropdown, Select, Pagination, Tooltip } from 'antd';
import type { MenuProps } from 'antd';
import {
  SearchOutlined,
  FilterOutlined,
  SortAscendingOutlined,
  ArrowRightOutlined,
  FileAddOutlined,
  PlusOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { literatureService, Literature } from '../../services/literature/literatureService';
import { LiteratureLimsShell } from '../../components/literature/LiteratureLimsShell';
import '../../styles/literature-stitch.css';
import '../../styles/notebook-stitch.css';

const PAGE_SIZE = 8;
const NOTE_PREVIEW = 420;

type SortKey = 'recent' | 'titleAsc' | 'titleDesc';
type FilterKey = 'all' | 'tagged';

function norm(s: string | undefined): string {
  return (s || '').toLowerCase();
}

function matchesSearch(item: Literature, q: string): boolean {
  const t = q.trim().toLowerCase();
  if (!t) return true;
  const hay = [
    item.title,
    ...(item.authors || []),
    item.journal,
    String(item.year ?? ''),
    ...(item.tags || []),
    (item.notes || '').replace(/<[^>]*>/g, ' ')
  ]
    .join(' ')
    .toLowerCase();
  return hay.includes(t);
}

const Notebook: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [items, setItems] = useState<Literature[]>([]);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('recent');
  const [filterKey, setFilterKey] = useState<FilterKey>('all');
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await literatureService.getList({ page: 1, limit: 1000, sortBy: 'updatedAt', sortOrder: 'desc' });
      const list = (data?.literatures ?? []) as Literature[];
      const withNotes = list.filter((l) => (l.notes || '').trim().length > 0);
      setItems(withNotes);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string };
      message.error(err.response?.data?.message || err.message || '加载笔记失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    let rows = items.filter((l) => matchesSearch(l, search));
    if (filterKey === 'tagged') {
      rows = rows.filter((l) => Array.isArray(l.tags) && l.tags.length > 0);
    }
    return rows;
  }, [items, search, filterKey]);

  const sorted = useMemo(() => {
    const rows = [...filtered];
    if (sortKey === 'titleAsc') {
      rows.sort((a, b) => norm(a.title).localeCompare(norm(b.title), 'zh-Hans'));
    } else if (sortKey === 'titleDesc') {
      rows.sort((a, b) => norm(b.title).localeCompare(norm(a.title), 'zh-Hans'));
    } else {
      rows.sort((a, b) => {
        const ta = new Date(a.updatedAt || 0).getTime();
        const tb = new Date(b.updatedAt || 0).getTime();
        return tb - ta;
      });
    }
    return rows;
  }, [filtered, sortKey]);

  useEffect(() => {
    setPage(1);
  }, [search, sortKey, filterKey]);

  const total = sorted.length;
  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, page]);

  const openInspect = (id: string) => {
    navigate(`/literature?inspect=${encodeURIComponent(id)}`);
  };

  const filterMenuItems: MenuProps['items'] = [
    {
      key: 'all',
      label: '全部笔记',
      onClick: () => setFilterKey('all')
    },
    {
      key: 'tagged',
      label: '仅含标签',
      onClick: () => setFilterKey('tagged')
    }
  ];

  const filterLabel = filterKey === 'tagged' ? '筛选 · 仅含标签' : '筛选';

  const rangeText = useMemo(() => {
    if (total === 0) return '共 0 条';
    const start = (page - 1) * PAGE_SIZE + 1;
    const end = Math.min(page * PAGE_SIZE, total);
    return `第 ${start}–${end} 条，共 ${total} 条`;
  }, [page, total]);

  const renderNotePreview = (notes: string | undefined) => {
    const plain = (notes || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const cut = plain.slice(0, NOTE_PREVIEW);
    return cut + (plain.length > NOTE_PREVIEW ? '…' : '');
  };

  const emptyLibrary = !loading && items.length === 0;
  const emptyFilter = !loading && items.length > 0 && filtered.length === 0;

  return (
    <LiteratureLimsShell
      rootClassName="lit-notebook-shell"
      crumbs={[
        { label: '首页', path: '/dashboard' },
        { label: '文献管理', path: '/literature' },
        { label: '笔记本' }
      ]}
      title="研究笔记本"
      subtitle="集中浏览各文献的研究笔记与要点；支持按标题、作者、期刊、标签与笔记内容检索。"
    >
      <div className="lit-notebook-page">
        <div className="lit-notebook-toolbar">
          <div className="lit-notebook-search">
            <SearchOutlined style={{ color: 'var(--st-on-surface-variant, #42474f)', fontSize: 18 }} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="按文献标题、作者、期刊、标签或笔记正文搜索…"
              aria-label="搜索笔记"
            />
          </div>
          <div className="lit-notebook-toolbar-actions">
            <Dropdown menu={{ items: filterMenuItems }} trigger={['click']}>
              <button type="button" className="lit-notebook-ghost-btn">
                <FilterOutlined />
                {filterLabel}
              </button>
            </Dropdown>
            <Select
              className="lit-notebook-select"
              value={sortKey}
              onChange={(v) => setSortKey(v as SortKey)}
              style={{ minWidth: 148 }}
              options={[
                { value: 'recent', label: '排序：最近更新' },
                { value: 'titleAsc', label: '排序：标题 A→Z' },
                { value: 'titleDesc', label: '排序：标题 Z→A' }
              ]}
              suffixIcon={<SortAscendingOutlined style={{ fontSize: 14, color: 'var(--st-on-surface-variant)' }} />}
            />
          </div>
        </div>

        <section className="lit-notebook-section">
          <div className="lit-notebook-section-head">
            <h2>笔记列表</h2>
            <span className="lit-notebook-count-badge">{total} 条</span>
          </div>

          {loading ? (
            <div className="lit-notebook-spin">
              <Spin size="large" />
            </div>
          ) : emptyLibrary ? (
            <div className="lit-notebook-empty">
              <div className="lit-notebook-empty-icon">
                <FileAddOutlined />
              </div>
              <h3>暂无笔记</h3>
              <p>在文献库中打开某篇文献的检视面板，记录并保存笔记后，将自动出现在此列表。</p>
              <button type="button" className="lit-notebook-primary-btn" onClick={() => navigate('/literature')}>
                前往文献管理
              </button>
            </div>
          ) : emptyFilter ? (
            <div className="lit-notebook-empty">
              <div className="lit-notebook-empty-icon">
                <SearchOutlined />
              </div>
              <h3>没有匹配的条目</h3>
              <p>请尝试更换关键词，或将筛选改回「全部笔记」。</p>
              <button
                type="button"
                className="lit-notebook-primary-btn"
                onClick={() => {
                  setSearch('');
                  setFilterKey('all');
                }}
              >
                清除条件
              </button>
            </div>
          ) : (
            <>
              {paged.map((item) => (
                <article key={item._id} className="lit-notebook-article">
                  <div className="lit-notebook-article-row">
                    <button type="button" className="lit-notebook-title" onClick={() => openInspect(item._id)}>
                      {item.title}
                    </button>
                    <button type="button" className="lit-notebook-open" onClick={() => openInspect(item._id)}>
                      打开检视 <ArrowRightOutlined />
                    </button>
                  </div>
                  <div className="lit-notebook-pills">
                    {(item.authors || []).slice(0, 6).map((a, i) => (
                      <span key={`${item._id}-a-${i}`} className="lit-notebook-pill">
                        {a}
                      </span>
                    ))}
                    {item.journal ? <span className="lit-notebook-pill lit-notebook-pill--journal">{item.journal}</span> : null}
                    {item.year != null ? (
                      <span className="lit-notebook-pill lit-notebook-pill--year">{item.year}</span>
                    ) : null}
                    {(item.tags || []).slice(0, 6).map((t, i) => (
                      <span key={`${item._id}-t-${i}`} className="lit-notebook-pill">
                        {t}
                      </span>
                    ))}
                  </div>
                  <div className="lit-notebook-body">
                    <p>{renderNotePreview(item.notes)}</p>
                  </div>
                </article>
              ))}

              <div className="lit-notebook-footer">
                <span className="lit-notebook-footer-meta">{rangeText}</span>
                <Pagination
                  size="small"
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  showSizeChanger={false}
                  onChange={(p) => setPage(p)}
                  hideOnSinglePage
                />
              </div>
            </>
          )}
        </section>
      </div>

      <Tooltip title="前往文献库，打开检视以记录笔记" placement="left">
        <button
          type="button"
          className="lit-notebook-fab"
          aria-label="前往文献库"
          onClick={() => navigate('/literature')}
        >
          <PlusOutlined />
        </button>
      </Tooltip>
    </LiteratureLimsShell>
  );
};

export default Notebook;
